import type { SubscriptionDetails } from "schemas/generated/billing";
import type { ReploSimpleProjectWithStats } from "schemas/generated/project";
import type { Workspace } from "schemas/generated/workspace";

import * as React from "react";

import { useCurrentWorkspace } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useOpenModal } from "@editor/hooks/useModal";
import { projectRouter } from "@editor/trpc/project";
import { subscriptionRouter } from "@editor/trpc/subscription";

import Button from "@replo/design-system/components/button/Button";
import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { ToggleGroup } from "@replo/design-system/components/toggle/ToggleGroup";
import { skipToken } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { Outlet } from "react-router-dom";

import V1Billing from "./v1/V1Billing";
import V2Billing from "./v2/V2Billing";

const Billing = () => {
  const workspace = useCurrentWorkspace();
  const projectId = useCurrentProjectId();

  // NOTE (Cole, 2025-08-27): We use project id in case we put the billing page in a project context for some reason
  const { data: activeSubscription, isSuccess: isActiveSubscriptionSuccess } =
    subscriptionRouter.getActiveSubscription.useQuery(
      workspace?.id ? { workspaceId: workspace.id, projectId } : skipToken,
    );

  const { data: workspaceProjects, isSuccess: isWorkspaceProjectsSuccess } =
    projectRouter.listWithStats.useQuery(
      workspace?.id ? { workspaceId: workspace.id } : skipToken,
    );

  if (
    !isActiveSubscriptionSuccess ||
    !isWorkspaceProjectsSuccess ||
    !workspace
  ) {
    return null;
  }

  return (
    <SubscriptionBasedBilling
      activeSubscription={activeSubscription ?? undefined}
      workspace={workspace}
      workspaceProjects={workspaceProjects.projects}
    />
  );
};

const SubscriptionBasedBilling: React.FC<{
  activeSubscription?: SubscriptionDetails;
  workspace: Workspace;
  workspaceProjects: ReploSimpleProjectWithStats[];
}> = ({ activeSubscription, workspace, workspaceProjects }) => {
  const v1Projects = workspaceProjects.filter(
    (project) => project.hostingType === "shopify",
  );
  const v2Projects = workspaceProjects.filter(
    (project) => project.hostingType === "replo",
  );

  const hasV1Projects = v1Projects.length > 0;
  const hasV2Projects = v2Projects.length > 0;
  const hasAnyProjects = hasV1Projects || hasV2Projects;

  const workspaceIsOnV1PaidPlan =
    (activeSubscription?.v1BillingTier ?? "free") !== "free";
  const workspaceIsOnV2PaidPlan =
    (activeSubscription?.v2BillingTier ?? "free") !== "free";

  const isPreferredBillingShopify =
    workspace?.preferredPaymentProcessor === "shopify";

  /*
    Based on flowchart here:
    https://www.figma.com/board/iXpnwAxADUdNXVpnFxWo32/Billing-Page-State-Flowchart?node-id=5-206&t=55QRWTIHB7TiTSQu-4
  **/
  const isBillingToggleVisible = React.useMemo(() => {
    if (isPreferredBillingShopify) {
      return false;
    }

    // They must have something on v2 in this case, with nothing on v1
    if (!workspaceIsOnV1PaidPlan && !hasV1Projects) {
      return false;
    }

    // They must have something on v1 in this case, with nothing on v2
    if (!workspaceIsOnV2PaidPlan && !hasV2Projects) {
      return false;
    }

    return true;
  }, [
    isPreferredBillingShopify,
    hasV1Projects,
    hasV2Projects,
    workspaceIsOnV1PaidPlan,
    workspaceIsOnV2PaidPlan,
  ]);

  /*
    Based on flowchart here:
    https://www.figma.com/board/iXpnwAxADUdNXVpnFxWo32/Billing-Page-State-Flowchart?node-id=5-384&t=55QRWTIHB7TiTSQu-4
  **/
  const isDefaultViewV2 = React.useMemo(() => {
    if (isPreferredBillingShopify) {
      return false;
    }

    if (workspaceIsOnV1PaidPlan && !workspaceIsOnV2PaidPlan) {
      return false;
    }

    if (
      !workspaceIsOnV1PaidPlan &&
      hasV1Projects &&
      !workspaceIsOnV2PaidPlan &&
      !hasV2Projects
    ) {
      return false;
    }

    return true;
  }, [
    isPreferredBillingShopify,
    workspaceIsOnV1PaidPlan,
    workspaceIsOnV2PaidPlan,
    hasV1Projects,
    hasV2Projects,
  ]);

  const [billingView, setBillingView] = React.useState<"v1" | "v2">(
    isDefaultViewV2 ? "v2" : "v1",
  );

  const userIsPayingOnMonthlyInterval =
    activeSubscription?.billingInterval === "monthly";

  const [isYearly, setIsYearly] = React.useState(
    !userIsPayingOnMonthlyInterval,
  );

  // If no projects exist, show the empty state
  if (!hasAnyProjects) {
    return <NoShopsEmptyState workspaceId={workspace.id} />;
  }

  return (
    <div className="flex w-full flex-col items-center gap-4 px-6">
      {isBillingToggleVisible && (
        <ToggleGroup
          selectedValue={billingView}
          onChange={(value) => setBillingView(value as "v1" | "v2")}
          options={[
            {
              value: "v2",
              label: (
                <div className="flex flex-col items-center justify-center gap-1">
                  <div className="flex items-center justify-center gap-2">
                    <span className="font-editorial text-lg">Replo AI</span>
                    <Tag size="sm" variant="beta">
                      BETA
                    </Tag>
                  </div>
                  <span className="text-sm">AI-Native Chat + Editor</span>
                </div>
              ),
            },
            {
              value: "v1",
              label: (
                <div className="flex flex-col items-center justify-center gap-1">
                  <span className="font-medium text-base">
                    Shopify Theme Customizer
                  </span>
                  <span className="text-sm">Editor for Shopify Themes</span>
                </div>
              ),
            },
          ]}
          layoutClassName="min-h-14 max-w-120"
          UNSAFE_className="rounded-full"
          UNSAFE_sliderClassName="rounded-full"
          size="base"
        />
      )}

      {billingView === "v2" ? (
        <V2Billing
          activeSubscription={activeSubscription}
          isYearly={isYearly}
          setIsYearly={setIsYearly}
        />
      ) : (
        <V1Billing
          activeSubscription={activeSubscription}
          isYearly={isYearly}
          setIsYearly={setIsYearly}
        />
      )}

      <Outlet />
    </div>
  );
};

const NoShopsEmptyState: React.FC<{ workspaceId: string }> = ({
  workspaceId,
}) => {
  const openModal = useOpenModal();

  const handleCreateShop = () => {
    openModal({
      type: "createShopFlowModal",
      props: {
        workspaceId,
      },
    });
  };

  return (
    <div className="flex w-full flex-col items-center justify-center px-6 pt-24">
      <EmptyState
        title="Create a shop to start"
        description="You need to create a shop before you can manage your subscription."
        variant="primary"
        size="base"
        icon={<Plus size={24} />}
      >
        <Button
          variant="primary"
          size="base"
          onClick={handleCreateShop}
          startEnhancer={<Plus size={16} />}
        >
          New Shop
        </Button>
      </EmptyState>
    </div>
  );
};

export default Billing;
